// src/Forecast.js

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Plot from "react-plotly.js";
import {
  fetchGlobalForecast,
  fetchGlobalWinrate,
  fetchFamilyForecast,
  fetchFamilyWinrate,
  fetchFamilyDetailsByMode,
  fetchSkuForecast,
  fetchSkuWinrate,
  downloadGlobalForecast,
  downloadFamilyForecast,
  downloadSkuForecast,
  downloadAllSkusForecast,
  downloadGlobalWinrate,
  downloadFamilyWinrate,
  downloadSkuWinrate,
  downloadAllSkusWinrate,
  fetchSimilarSkus,
} from "./api";
import { getForecastChartConfig, getWinrateChartConfig, aggregateByWeek, aggregateWinrateByWeek, formatPeriod } from "./chartData";
import "./App.css";

// --- Icone ---
const iconVisible = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,168a40,40,0,1,1,40-40A40,40,0,0,1,128,168Z"></path>
  </svg>
);
const iconHidden = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M96.68,57.87a4,4,0,0,1,2.08-6.6A130.13,130.13,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41a8,8,0,0,1,0,6.5c-.35.79-8.82,19.57-27.65,38.4q-4.28,4.26-8.79,8.07a4,4,0,0,1-5.55-.36ZM213.92,210.62a8,8,0,1,1-11.84,10.76L180,197.13A127.21,127.21,0,0,1,128,208c-34.88,0-66.57-13.26-91.66-38.34C17.51,150.83,9,132.05,8.69,131.26a8,8,0,0,1,0-6.5C9,124,17.51,105.18,36.34,86.35a135,135,0,0,1,25-19.78L42.08,45.38A8,8,0,1,1,53.92,34.62Zm-65.49-48.25-52.69-58a40,40,0,0,0,52.69,58Z"></path>
  </svg>
);

const iconDownload = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M222,144v64a6,6,0,0,1-6,6H40a6,6,0,0,1-6-6V144a6,6,0,0,1,12,0v58H210V144a6,6,0,0,1,12,0Zm-98.24,4.24a6,6,0,0,0,8.48,0l40-40a6,6,0,0,0-8.48-8.48L134,129.51V32a6,6,0,0,0-12,0v97.51L92.24,99.76a6,6,0,0,0-8.48,8.48Z"></path>
  </svg>
);

const DEFAULT_SKU = "0016J001612C62";

const levelOptions = ["GLOBAL", "FAMILY", "SKU"];

const forecastColumns = [
  "MONTH",
  "ACT QTY",
  "ACT QTY Y-1",
  "FCST QTY - σ",
  "FCST QTY (avg)",
  "FCST QTY + σ",
  "ACT VAL Y-1",
  "FCST VALUE - σ",
  "FCST VALUE (avg)",
  "FCST VALUE + σ",
];

const forecastMap = {
  MONTH: "year_week",
  "ACT QTY": "actual_qty",
  "ACT QTY Y-1": "actual_qty_last_year",
  "FCST QTY - σ": "fcst_qty_lowerbound",
  "FCST QTY (avg)": "fcst_qty_avg",
  "FCST QTY + σ": "fcst_qty_upperbound",
  "ACT VAL Y-1": "actual_val_last_year",
  "FCST VALUE - σ": "fcst_val_lowerbound",
  "FCST VALUE (avg)": "fcst_val_avg",
  "FCST VALUE + σ": "fcst_val_upperbound",
};

const winrateColumns = [
  "MONTH",
  "ACT WR Y-1 RAW",
  "FCST WR (avg) RAW",
  "ACT WR Y-1 SHRINKED",
  "FCST WR (avg) SHRINKED",
];

const winrateMap = {
  MONTH: "year_week",
  "ACT WR Y-1 RAW": "actual_wr_last_year",
  "FCST WR (avg) RAW": "fcst_qty_avg",
  "ACT WR Y-1 SHRINKED": "actual_wr_last_year_shrinked",
  "FCST WR (avg) SHRINKED": "fcst_qty_avg_shrinked",
};

// Hook per salvare/caricare da localStorage
function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    try {
      const stickyValue = window.localStorage.getItem(key);
      return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
    } catch (e) {
      console.warn("Error reading from localStorage", e);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn("Error writing to localStorage", e);
    }
  }, [key, value]);

  return [value, setValue];
}

function Forecast() {
  // Stati per i filtri
  const [searchParams] = useSearchParams();

  // 1. Leggiamo i parametri dall'URL all'avvio
  const urlLevel = searchParams.get("level");
  const urlEntity = searchParams.get("entity");

  // 2. Inizializziamo lo stato con i valori dell'URL (o i default se non presenti)
  const [level, setLevel] = useState(urlLevel || "GLOBAL");
  const [entity, setEntity] = useState(urlEntity || "");
  const [inputValue, setInputValue] = useState(urlEntity || "");

  const [entityOptions, setEntityOptions] = useState([]);
  // --- STATI DATI DINAMICI ---
  const [allForecastData, setAllForecastData] = useState([]);
  const [forecastData, setForecastData] = useState([]);
  const [availableRuns, setAvailableRuns] = useState([]);
  const [selectedGenerationIndex, setSelectedGenerationIndex] = useState(0);
  const [forecastMode, setForecastMode] = useState("demand");
  const [customerType, setCustomerType] = useState("combined");
  const [availabilityType, setAvailabilityType] = useState("available");
  const [winrateData, setWinrateData] = useState([]);
  const [allWinrateData, setAllWinrateData] = useState([]);
  const [winrateRuns, setWinrateRuns] = useState([]);
  const [selectedWinrateGenIndex, setSelectedWinrateGenIndex] = useState(0);
  const [loading, setLoading] = useState(false); // Inizia false finché non scatta l'effect
  const [isDownloadingForecast, setIsDownloadingForecast] = useState(false);
  const [isDownloadingAllSkus, setIsDownloadingAllSkus] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [isDownloadingWinrate, setIsDownloadingWinrate] = useState(false);
  const [isDownloadingAllSkusWinrate, setIsDownloadingAllSkusWinrate] = useState(false);
  const [isWinrateDropdownOpen, setIsWinrateDropdownOpen] = useState(false);
  const columnsRef = useRef(null);

  const [skuSuggestions, setSkuSuggestions] = useState([]);
  const [isSearchingSku, setIsSearchingSku] = useState(false);
  const searchDebounceRef = useRef(null);

  // Stati per i controlli UI
  const [isCompareActive, setIsCompareActive] = useState(false);
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);

  const [popupChart, setPopupChart] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const filteredForecastData = useMemo(
    () => aggregateByWeek(forecastData, customerType),
    [forecastData, customerType]
  );
  const filteredWinrateData = useMemo(
    () => aggregateWinrateByWeek(winrateData, forecastData, customerType),
    [winrateData, forecastData, customerType]
  );

  const [isWinrateCompareActive, setIsWinrateCompareActive] = useState(false);
  const [oppositeWinrateData, setOppositeWinrateData] = useState([]);

  useEffect(() => {
    setOppositeWinrateData([]);
    if (!isWinrateCompareActive) return undefined;
    const params = {
      customer_type: customerType,
      availability: availabilityType === "available" ? "unavailable" : "available",
    };
    let request = null;
    if (level === "GLOBAL") request = fetchGlobalWinrate({ page_size: 3000, ...params });
    else if (level === "FAMILY" && entity) request = fetchFamilyWinrate(entity, { page_size: 3000, ...params });
    else if (level === "SKU" && entity) request = fetchSkuWinrate(entity, { page_size: 3700, ...params });
    if (!request) return undefined;
    let cancelled = false;
    request
      .then((res) => {
        if (!cancelled) setOppositeWinrateData(res?.forecast_result || []);
      })
      .catch((err) => console.error("Failed to fetch comparison win rate:", err));
    return () => {
      cancelled = true;
    };
  }, [isWinrateCompareActive, level, entity, customerType, availabilityType]);

  const winrateCompareMap = useMemo(() => {
    if (!isWinrateCompareActive) return null;
    const runId = winrateRuns[selectedWinrateGenIndex]?.run_id;
    const rows = oppositeWinrateData.filter((r) => r.run_id === runId);
    return new Map(
      aggregateWinrateByWeek(rows, forecastData, customerType).map((r) => [r.year_week, r]),
    );
  }, [isWinrateCompareActive, oppositeWinrateData, winrateRuns, selectedWinrateGenIndex, forecastData, customerType]);

  const showWrCol = (col) => winrateColumnVisibility[col];

  const renderWinrateCell = (row, field) => {
    const format = (v) => (v != null ? (v * 100).toFixed(1) + "%" : "—");
    const base = row[field];
    const target = isWinrateCompareActive ? winrateCompareMap?.get(row.year_week)?.[field] : null;
    if (base == null || target == null) return format(base);
    const delta = target - base;
    const deltaClass =
      delta === 0 ? "compare-neutral" : delta > 0 ? "compare-improvement" : "compare-degradation";
    const deltaLabel =
      delta === 0
        ? "0%"
        : base !== 0
          ? `${delta > 0 ? "+" : ""}${((delta / Math.abs(base)) * 100).toFixed(1)}%`
          : "—";
    return (
      <span
        className="compare-cell-content"
        title={
          availabilityType === "available"
            ? `Available ${format(base)} → Unavailable ${format(target)} (${deltaLabel})`
            : `Unavailable ${format(base)} → Available ${format(target)} (${deltaLabel})`
        }
      >
        <span className="compare-target">{format(base)}</span>
        <span className={`compare-delta ${deltaClass}`}>{deltaLabel}</span>
      </span>
    );
  };

  const comparisonData = useMemo(() => {
    if (!isCompareActive) return null;
    if (forecastMode === "demand") {
      if (availableRuns.length === 0) return null;
      const currentRunId = availableRuns[0].run_id;
      return aggregateByWeek(allForecastData.filter((row) => row.run_id === currentRunId), customerType);
    } else {
      if (winrateRuns.length === 0) return null;
      const currentRunId = winrateRuns[0].run_id;
      const compDemand = availableRuns.length > 0
        ? allForecastData.filter((row) => row.run_id === availableRuns[0].run_id)
        : forecastData;
      return aggregateWinrateByWeek(allWinrateData.filter((row) => row.run_id === currentRunId), compDemand, customerType);
    }
  }, [isCompareActive, forecastMode, availableRuns, allForecastData, winrateRuns, allWinrateData, customerType, forecastData]);

  // Configurazione colonne (Salvata in LocalStorage)
  const [columnVisibility, setColumnVisibility] = useStickyState(
    (() => {
      const initialState = forecastColumns.reduce(
        (acc, col) => ({ ...acc, [col]: true }),
        {}
      );
      // Impostazione di default iniziale se non c'è nulla nel localStorage
      initialState["ACT QTY"] = false;
      return initialState;
    })(),
    "forecastPage_visibility_v2"
  );

  const [winrateColumnVisibility, setWinrateColumnVisibility] = useStickyState(
    winrateColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
    "forecastPage_winrateVisibility_v3"
  );

  useEffect(() => {
    const reconcile = (setter, columns) =>
      setter((prev) => {
        const next = {};
        let changed = Object.keys(prev).some((key) => !columns.includes(key));
        columns.forEach((col) => {
          if (prev[col] === undefined) changed = true;
          next[col] = prev[col] === undefined ? true : prev[col];
        });
        return changed ? next : prev;
      });
    reconcile(setColumnVisibility, forecastColumns);
    reconcile(setWinrateColumnVisibility, winrateColumns);
  }, [setColumnVisibility, setWinrateColumnVisibility]);

  const handleChartClick = (type, name, title) => {
    setPopupChart({ type, name, title });
  };

  const closePopup = () => setPopupChart(null);

  // Helpers formattazione
  const formatCurrency = (num) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(num || 0);

  const formatNumber = (num) =>
    Math.round(Number(num || 0)).toLocaleString("en-US");


  const processForecastData = (rawData) => {
    if (!rawData || rawData.length === 0) return { runs: [], sortedData: [] };
    const runMap = new Map();
    rawData.forEach((row) => {
      if (!runMap.has(row.run_id)) {
        runMap.set(row.run_id, {
          run_id: row.run_id,
          run_date: row.run_date,
          parsedDate: new Date(row.run_date),
        });
      }
    });
    const sortedRuns = Array.from(runMap.values()).sort((a, b) => {
      return b.parsedDate - a.parsedDate;
    });
    return { runs: sortedRuns, sortedData: rawData };
  };

  const handleLevelChange = (e) => {
    const newLevel = e.target.value;
    setLevel(newLevel);

    if (newLevel === "FAMILY") {
      if (entityOptions.length > 0) {
        setEntity(entityOptions[0]);
        setInputValue(entityOptions[0]); // Sincronizza input
      } else {
        setEntity("");
        setInputValue("");
      }
    } else if (newLevel === "SKU") {
      setEntity(DEFAULT_SKU);
      setInputValue(DEFAULT_SKU); // Sincronizza input con default
    } else if (newLevel === "GLOBAL") {
      setEntity("");
      setInputValue("");
    }
  };

  useEffect(() => {
    const loadEntities = async () => {
      try {
        const data = await fetchFamilyDetailsByMode("constrained");

        let listToProcess = [];
        if (data?.family_list) {
          listToProcess = data.family_list;
        } else if (data?.family_details) {
          listToProcess = data.family_details;
        } else if (Array.isArray(data)) {
          listToProcess = data;
        }

        if (listToProcess.length > 0) {
          const families = [...new Set(
            listToProcess
              .filter((item) => item && item.family)
              .map((item) => item.family)
          )].sort();

          setEntityOptions(families);
        }
      } catch (error) {
        console.error("Unable to load the list of families:", error);
      }
    };

    loadEntities();
  }, []);

  // --- EFFECT: Caricamento Dati ---
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setForecastData([]);

      try {
        let result = null;
        let wrResult = null;

        if (level === "GLOBAL") {
          [result, wrResult] = await Promise.allSettled([
            fetchGlobalForecast({ page_size: 3000, customer_type: customerType }),
            fetchGlobalWinrate({ page_size: 3000, customer_type: customerType, availability: availabilityType }),
          ]).then((r) => r.map((x) => (x.status === "fulfilled" ? x.value : null)));
        } else if (level === "FAMILY" && entity) {
          [result, wrResult] = await Promise.allSettled([
            fetchFamilyForecast(entity, { page_size: 3000, customer_type: customerType }),
            fetchFamilyWinrate(entity, { page_size: 3000, customer_type: customerType, availability: availabilityType }),
          ]).then((r) => r.map((x) => (x.status === "fulfilled" ? x.value : null)));
        } else if (level === "SKU" && entity) {
          [result, wrResult] = await Promise.allSettled([
            fetchSkuForecast(entity, { page_size: 3700, customer_type: customerType }),
            fetchSkuWinrate(entity, { page_size: 3700, customer_type: customerType, availability: availabilityType }),
          ]).then((r) => r.map((x) => (x.status === "fulfilled" ? x.value : null)));
        }

        // Demand data
        const rawRows = result?.forecast_result || [];
        const { runs } = processForecastData(rawRows);
        setAllForecastData(rawRows);
        setAvailableRuns(runs);
        if (runs.length > 0) {
          setForecastData(rawRows.filter((r) => r.run_id === runs[0].run_id));
          setSelectedGenerationIndex(0);
        } else {
          setForecastData([]);
        }

        // Winrate data
        const wrRows = wrResult?.forecast_result || [];
        const { runs: wrRuns } = processForecastData(wrRows);
        setAllWinrateData(wrRows);
        setWinrateRuns(wrRuns);
        if (wrRuns.length > 0) {
          setWinrateData(wrRows.filter((r) => r.run_id === wrRuns[0].run_id));
          setSelectedWinrateGenIndex(0);
        } else {
          setWinrateData([]);
        }
      } catch (error) {
        console.error(`Errore caricamento forecast (${level}):`, error);
        setForecastData([]);
        setWinrateData([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [level, entity, customerType, availabilityType]); // Ricarica quando cambia il livello o il customer type

  // Chiude il dropdown se si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDownloadDropdownOpen &&
        !event.target.closest(".split-btn-container")
      ) {
        setIsDownloadDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDownloadDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isWinrateDropdownOpen &&
        !event.target.closest(".split-btn-container")
      ) {
        setIsWinrateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isWinrateDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target)) {
        setIsColumnDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownloadForecast = async () => {
    // 1. Verifica che ci sia una run selezionata
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = availableRuns[selectedGenerationIndex];
    const currentRunId = currentRun.run_id;

    setIsDownloadingForecast(true);

    try {
      // 2. Logica condizionale in base al LEVEL selezionato
      if (level === "GLOBAL") {
        const filename = `forecast_GLOBAL_${currentRunId}_${customerType}.xlsx`;
        await downloadGlobalForecast({ run_id: currentRunId, customer_type: customerType }, filename);
      } else if (level === "FAMILY") {
        if (!entity) {
          alert("Please select a Family first.");
          setIsDownloadingForecast(false);
          return;
        }
        const filename = `forecast_FAMILY_${entity}_${currentRunId}_${customerType}.xlsx`;
        await downloadFamilyForecast(
          entity,
          { run_id: currentRunId, customer_type: customerType },
          filename
        );
      } else if (level === "SKU") {
        if (!entity) {
          alert("Please enter an SKU ID first.");
          setIsDownloadingForecast(false);
          return;
        }
        const safeEntity = entity.replace(/[^a-z0-9]/gi, "_");
        const filename = `forecast_SKU_${safeEntity}_${currentRunId}_${customerType}.xlsx`;

        await downloadSkuForecast(entity, { run_id: currentRunId, customer_type: customerType }, filename);
      }
    } catch (error) {
      console.error(error);
      alert("Error downloading forecast file.");
    } finally {
      setIsDownloadingForecast(false);
    }
  };

  const handleDownloadAllSkus = async () => {
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = availableRuns[selectedGenerationIndex];
    const currentRunId = currentRun.run_id;

    setIsDownloadingAllSkus(true);

    try {
      const params = { run_id: currentRunId };
      let filename = `all_skus_forecast_${currentRunId}_unfiltered.xlsx`;

      if (level === "FAMILY" && entity) {
        params.family = entity;
        filename = `family_${entity}_skus_forecast_${currentRunId}_unfiltered.xlsx`;
      }

      await downloadAllSkusForecast(params, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading all SKUs file.");
    } finally {
      setIsDownloadingAllSkus(false);
    }
  };

  const handleDownloadForecastUnfiltered = async () => {
    setIsDownloadDropdownOpen(false);
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }
    if (level !== "GLOBAL" && !entity) {
      alert(level === "FAMILY" ? "Please select a Family first." : "Please enter an SKU ID first.");
      return;
    }

    const currentRunId = availableRuns[selectedGenerationIndex].run_id;
    const params = { run_id: currentRunId };

    setIsDownloadingForecast(true);
    try {
      if (level === "GLOBAL") {
        await downloadGlobalForecast(params, `forecast_GLOBAL_${currentRunId}_unfiltered.xlsx`);
      } else if (level === "FAMILY") {
        await downloadFamilyForecast(entity, params, `forecast_FAMILY_${entity}_${currentRunId}_unfiltered.xlsx`);
      } else {
        const safeEntity = entity.replace(/[^a-z0-9]/gi, "_");
        await downloadSkuForecast(entity, params, `forecast_SKU_${safeEntity}_${currentRunId}_unfiltered.xlsx`);
      }
    } catch (error) {
      console.error(error);
      alert("Error downloading forecast file.");
    } finally {
      setIsDownloadingForecast(false);
    }
  };

  const handleDownloadWinrateUnfiltered = async () => {
    setIsWinrateDropdownOpen(false);
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }
    if (level !== "GLOBAL" && !entity) {
      alert(level === "FAMILY" ? "Please select a Family first." : "Please enter an SKU ID first.");
      return;
    }

    const currentRunId = winrateRuns[selectedWinrateGenIndex].run_id;
    const params = { run_id: currentRunId, availability: "all" };

    setIsDownloadingWinrate(true);
    try {
      if (level === "GLOBAL") {
        await downloadGlobalWinrate(params, `global_winrate_${currentRunId}_unfiltered.xlsx`);
      } else if (level === "FAMILY") {
        await downloadFamilyWinrate(entity, params, `winrate_FAMILY_${entity}_${currentRunId}_unfiltered.xlsx`);
      } else {
        const safeEntity = entity.replace(/[^a-z0-9]/gi, "_");
        await downloadSkuWinrate(entity, params, `winrate_SKU_${safeEntity}_${currentRunId}_unfiltered.xlsx`);
      }
    } catch (error) {
      console.error(error);
      alert("Error downloading win rate file.");
    } finally {
      setIsDownloadingWinrate(false);
    }
  };

  const handleDownloadWinrate = async () => {
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = winrateRuns[selectedWinrateGenIndex];
    const currentRunId = currentRun.run_id;

    setIsDownloadingWinrate(true);
    try {
      if (level === "GLOBAL") {
        const filename = `global_winrate_${currentRunId}_${customerType}_${availabilityType}.xlsx`;
        await downloadGlobalWinrate({ run_id: currentRunId, customer_type: customerType, availability: availabilityType }, filename);
      } else if (level === "FAMILY") {
        if (!entity) {
          alert("Please select a Family first.");
          setIsDownloadingWinrate(false);
          return;
        }
        const filename = `winrate_FAMILY_${entity}_${currentRunId}_${customerType}_${availabilityType}.xlsx`;
        await downloadFamilyWinrate(entity, { run_id: currentRunId, customer_type: customerType, availability: availabilityType }, filename);
      } else if (level === "SKU") {
        if (!entity) {
          alert("Please enter an SKU ID first.");
          setIsDownloadingWinrate(false);
          return;
        }
        const safeEntity = entity.replace(/[^a-z0-9]/gi, "_");
        const filename = `winrate_SKU_${safeEntity}_${currentRunId}_${customerType}_${availabilityType}.xlsx`;
        await downloadSkuWinrate(entity, { run_id: currentRunId, customer_type: customerType, availability: availabilityType }, filename);
      }
    } catch (error) {
      console.error(error);
      alert("Error downloading win rate file.");
    } finally {
      setIsDownloadingWinrate(false);
    }
  };

  const handleDownloadAllSkusWinrate = async () => {
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = winrateRuns[selectedWinrateGenIndex];
    const currentRunId = currentRun.run_id;

    setIsDownloadingAllSkusWinrate(true);
    try {
      const params = { run_id: currentRunId, availability: "all" };
      let filename = `all_skus_winrate_${currentRunId}_unfiltered.xlsx`;

      if (level === "FAMILY" && entity) {
        params.family = entity;
        filename = `family_${entity}_skus_winrate_${currentRunId}_unfiltered.xlsx`;
      }

      await downloadAllSkusWinrate(params, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading all SKUs win rate file.");
    } finally {
      setIsDownloadingAllSkusWinrate(false);
    }
  };

  const sortedForecastData = useMemo(() => {
    // A. Filtro base (rimuove righe vuote/nulle)
    let processedData = [...filteredForecastData].filter((row) => {
      return (
        row.fcst_qty_avg != null ||
        row.fcst_qty_lowerbound != null ||
        row.fcst_val_avg != null
      );
    });

    // B. Gestione Ordinamento
    if (sortConfig.key && sortConfig.direction) {
      const dataKey = forecastMap[sortConfig.key];

      processedData.sort((a, b) => {
        let aValue = a[dataKey];
        let bValue = b[dataKey];

        // Gestione valori nulli/undefined
        if (aValue == null)
          aValue = typeof bValue === "string" ? "" : -Infinity;
        if (bValue == null)
          bValue = typeof aValue === "string" ? "" : -Infinity;

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      // C. Ordinamento di Default (per Data/Week crescente)
      processedData.sort((a, b) => a.year_week.localeCompare(b.year_week));
    }

    return processedData;
  }, [filteredForecastData, sortConfig]);

  const sortedWinrateData = useMemo(() => {
    let processedData = [...filteredWinrateData].filter(
      (row) => (row.fcst_qty_avg != null && row.fcst_qty_avg !== 0) || (row.actual_wr_last_year != null && row.actual_wr_last_year !== 0)
    );
    if (sortConfig.key && sortConfig.direction) {
      const dataKey = winrateMap[sortConfig.key];
      if (dataKey) {
        processedData.sort((a, b) => {
          let aValue = a[dataKey];
          let bValue = b[dataKey];
          if (aValue == null) aValue = -Infinity;
          if (bValue == null) bValue = -Infinity;
          if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        });
      }
    } else {
      processedData.sort((a, b) => a.year_week.localeCompare(b.year_week));
    }
    return processedData;
  }, [filteredWinrateData, sortConfig]);

  // 2. Funzione Trigger Click
  const requestSort = (colName) => {
    let direction = "asc";
    if (sortConfig.key === colName && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === colName && sortConfig.direction === "desc") {
      direction = null; // Reset
    }
    setSortConfig({ key: direction ? colName : null, direction });
  };

  // 3. Indicatore Freccia
  const getSortIndicator = (colName) => {
    if (sortConfig.key !== colName) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const handleGenerationChange = (e) => {
    const newIndex = parseInt(e.target.value, 10);
    if (newIndex === 0) setIsCompareActive(false);

    if (forecastMode === "demand") {
      setSelectedGenerationIndex(newIndex);
      if (availableRuns[newIndex]) {
        setForecastData(allForecastData.filter((row) => row.run_id === availableRuns[newIndex].run_id));
      } else {
        setForecastData([]);
      }
    } else {
      setSelectedWinrateGenIndex(newIndex);
      if (winrateRuns[newIndex]) {
        setWinrateData(allWinrateData.filter((row) => row.run_id === winrateRuns[newIndex].run_id));
      } else {
        setWinrateData([]);
      }
    }
  };

  // Gestisce la digitazione nell'input SKU con debounce
  const handleSkuInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Reset timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Se vuoto, pulisci suggerimenti
    if (!value || value.trim() === "") {
      setSkuSuggestions([]);
      return;
    }

    // Avvia timer 1 secondo
    setIsSearchingSku(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await fetchSimilarSkus(value);
        if (response && response.skus) {
          setSkuSuggestions(response.skus);
        } else {
          setSkuSuggestions([]);
        }
      } catch (err) {
        console.error("Error searching SKUs", err);
        setSkuSuggestions([]);
      } finally {
        setIsSearchingSku(false);
      }
    }, 750);
  };

  // Gestisce la selezione di un SKU dalla lista
  const handleSelectSku = (selectedSku) => {
    setInputValue(selectedSku);
    setEntity(selectedSku);
    setSkuSuggestions([]);
  };

  return (
    <>
      <div className={`forecast-page ${popupChart ? "blur-background" : ""}`}>
        <header>
          <h1>FORECAST</h1>
          <div className="header-buttons">
            {level === "SKU" && entity && (
              <Link
                to={`/sku/${encodeURIComponent(entity)}`}
                className="header-btn"
              >
                SKU VIEW
              </Link>
            )}

            {level === "FAMILY" && entity && (
              <Link
                to={`/family/${encodeURIComponent(entity)}`}
                className="header-btn"
              >
                FAMILY VIEW
              </Link>
            )}

            <Link to="/" className="header-btn">
              GLOBAL VIEW
            </Link>
          </div>
        </header>

        <main className="dashboard-container">
          <section className="forecast-section">
            {/* --- SEZIONE FILTRI AGGIORNATA --- */}
            <div className="filter-section forecast-controls">
              {/* 1. LEVEL: Selezionabile (GLOBAL, FAMILY, SKU) */}
              <span>Level</span>
              <select value={level} onChange={handleLevelChange}>
                {levelOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              {/* 2. ENTITY: Disabilitato SOLO su GLOBAL */}
              <span>Entity</span>
              {level === "SKU" ? (
                <div className="filter-input-wrapper">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleSkuInputChange} // Usa la nuova funzione
                    placeholder="Type SKU ID..."
                    onBlur={() => setTimeout(() => setSkuSuggestions([]), 200)}
                    style={{ minWidth: "200px" }} // Opzionale: per allinearlo visivamente
                  />

                  {/* Spinner caricamento */}
                  {isSearchingSku && (
                    <div className="input-loading-spinner"></div>
                  )}

                  {/* Dropdown Suggerimenti */}
                  {skuSuggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                      {skuSuggestions.map((item, idx) => (
                        <div
                          key={idx}
                          className="suggestion-item"
                          onMouseDown={() => handleSelectSku(item.sku_id)}
                        >
                          {item.sku_id}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Select classica per Family (resta invariata) */
                <select
                  value={entity}
                  onChange={(e) => {
                    setEntity(e.target.value);
                    setInputValue(e.target.value);
                  }}
                  disabled={level === "GLOBAL"}
                  style={
                    level === "GLOBAL"
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : {}
                  }
                >
                  <option value="" disabled>
                    Select Entity
                  </option>
                  {entityOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}

              {/* 3. GENERATION: Dinamico in base ai dati scaricati */}
              <span>Generation</span>
              <select
                id="generation-filter"
                value={forecastMode === "demand" ? selectedGenerationIndex : selectedWinrateGenIndex}
                onChange={handleGenerationChange}
                disabled={loading || (forecastMode === "demand" ? availableRuns.length === 0 : winrateRuns.length === 0)}
              >
                {(forecastMode === "demand" ? availableRuns : winrateRuns).map((run, index) => {
                  const dateObj = new Date(run.run_date);
                  const label = `${formatPeriod(dateObj.toISOString().slice(0, 10))}, ${dateObj.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                    timeZone: "UTC",
                  })}`;
                  return (
                    <option key={run.run_id} value={index}>
                      {label}
                    </option>
                  );
                })}
                {(forecastMode === "demand" ? availableRuns : winrateRuns).length === 0 && !loading && (
                  <option value={0}>No Data Available</option>
                )}
              </select>
              <span>Customer</span>
              <select
                value={customerType}
                onChange={(e) => setCustomerType(e.target.value)}
              >
                <option value="combined">Combined</option>
                <option value="prime">Prime</option>
                <option value="reseller">Reseller</option>
              </select>

              {forecastMode === "winrate" && (
                <>
                  <span>Type</span>
                  <div className="availability-group">
                    <select
                      value={availabilityType}
                      onChange={(e) => setAvailabilityType(e.target.value)}
                    >
                      <option value="available">Available</option>
                      <option value="unavailable">Unavailable</option>
                    </select>
                    <label
                      className="compare-switch"
                      title={
                        availabilityType === "available"
                          ? "Compare Available → Unavailable"
                          : "Compare Unavailable → Available"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isWinrateCompareActive}
                        onChange={() => setIsWinrateCompareActive((prev) => !prev)}
                      />
                      <span className="compare-slider" />
                      <span className="compare-switch-label">
                        vs {availabilityType === "available" ? "Unavailable" : "Available"}
                      </span>
                    </label>
                  </div>
                </>
              )}
              <div className="compare-btn-wrapper">
                <button
                  className={`toggle-btn ${isCompareActive ? "active" : ""}`}
                  disabled={forecastMode === "demand" ? selectedGenerationIndex === 0 : selectedWinrateGenIndex === 0}
                  onClick={() => setIsCompareActive(!isCompareActive)}
                >
                  Compare to Current
                </button>
              </div>

              <div className="toggle-container">
                {forecastMode === "demand" ? (
                  <>
                    <div className="split-btn-container" style={{ marginRight: "10px", marginLeft: "10px" }}>
                      <button
                        onClick={handleDownloadForecast}
                        className={`split-main-btn ${isDownloadingForecast ? "loading" : ""}`}
                        disabled={isDownloadingForecast}
                        title="Download XLSX"
                      >
                        <div className="btn-icon">{iconDownload}</div>
                        <span>Download</span>
                      </button>
                      <button
                        className="split-arrow-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
                        }}
                      >
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                          style={{ transform: isDownloadDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {isDownloadDropdownOpen && (
                        <div className="split-dropdown-menu">
                          <button
                            onClick={handleDownloadForecastUnfiltered}
                            className={`split-dropdown-item ${isDownloadingForecast ? "loading" : ""}`}
                            disabled={isDownloadingForecast || availableRuns.length === 0}
                          >
                            <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                            <span>Download without filters</span>
                          </button>
                          {level !== "SKU" && (
                            <button
                              onClick={() => { handleDownloadAllSkus(); }}
                              className={`split-dropdown-item ${isDownloadingAllSkus ? "loading" : ""}`}
                              disabled={isDownloadingAllSkus || availableRuns.length === 0}
                            >
                              <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                              <span>Download all SKUs without filters</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="split-btn-container" style={{ marginRight: "10px", marginLeft: "10px" }}>
                      <button
                        onClick={handleDownloadWinrate}
                        className={`split-main-btn ${isDownloadingWinrate ? "loading" : ""}`}
                        disabled={isDownloadingWinrate}
                        title="Download Win Rate"
                      >
                        <div className="btn-icon">{iconDownload}</div>
                        <span>Download</span>
                      </button>
                      <button
                        className="split-arrow-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsWinrateDropdownOpen(!isWinrateDropdownOpen);
                        }}
                      >
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                          style={{ transform: isWinrateDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {isWinrateDropdownOpen && (
                        <div className="split-dropdown-menu">
                          <button
                            onClick={handleDownloadWinrateUnfiltered}
                            className={`split-dropdown-item ${isDownloadingWinrate ? "loading" : ""}`}
                            disabled={isDownloadingWinrate || winrateRuns.length === 0}
                          >
                            <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                            <span>Download without filters</span>
                          </button>
                          {level !== "SKU" && (
                            <button
                              onClick={() => { handleDownloadAllSkusWinrate(); }}
                              className={`split-dropdown-item ${isDownloadingAllSkusWinrate ? "loading" : ""}`}
                              disabled={isDownloadingAllSkusWinrate || winrateRuns.length === 0}
                            >
                              <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                              <span>Download all SKUs without filters</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="column-visibility-container" ref={columnsRef}>
                  <button
                    className="toggle-btn column-toggle-btn"
                    onClick={() =>
                      setIsColumnDropdownOpen(!isColumnDropdownOpen)
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="#f5f5f5"
                      viewBox="0 0 256 256"
                    >
                      <path d="M84,52V92H28a4,4,0,0,1-4-4V64A16,16,0,0,1,40,48H80A4,4,0,0,1,84,52Zm16,152a4,4,0,0,0,4,4h48a4,4,0,0,0,4-4V164H100ZM24,168v24a16,16,0,0,0,16,16H80a4,4,0,0,0,4-4V164H28A4,4,0,0,0,24,168Zm0-56v32a4,4,0,0,0,4,4H84V108H28A4,4,0,0,0,24,112ZM152,48H104a4,4,0,0,0-4,4V92h56V52A4,4,0,0,0,152,48Zm76,60H172v40h56a4,4,0,0,0,4-4V112A4,4,0,0,0,228,108ZM100,148h56V108H100ZM216,48H176a4,4,0,0,0-4,4V92h56a4,4,0,0,0,4-4V64A16,16,0,0,0,216,48Zm12,116H172v40a4,4,0,0,0,4,4h40a16,16,0,0,0,16-16V168A4,4,0,0,0,228,164Z"></path>
                    </svg>
                    <span>COLUMNS</span>
                  </button>
                  {isColumnDropdownOpen && (
                    <div className="column-dropdown show">
                      {(forecastMode === "demand" ? forecastColumns : winrateColumns)
                        .filter((col) => col !== "ACT QTY" && col !== "MONTH")
                        .map((colName) => (
                          <div key={colName} className="column-dropdown-item">
                            <span>{colName}</span>
                            <div
                              className="column-visibility-icon"
                              onClick={() => {
                                if (forecastMode === "demand") {
                                  setColumnVisibility((prev) => ({ ...prev, [colName]: !prev[colName] }));
                                } else {
                                  setWinrateColumnVisibility((prev) => ({ ...prev, [colName]: !prev[colName] }));
                                }
                              }}
                            >
                              {(forecastMode === "demand" ? columnVisibility : winrateColumnVisibility)[colName]
                                ? iconVisible
                                : iconHidden}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="forecast-mode-tabs">
              <button
                className={`forecast-mode-tab ${forecastMode === "demand" ? "active" : ""}`}
                onClick={() => setForecastMode("demand")}
              >
                Demand
              </button>
              <button
                className={`forecast-mode-tab ${forecastMode === "winrate" ? "active" : ""}`}
                onClick={() => setForecastMode("winrate")}
              >
                Win Rate
              </button>
            </div>

            {/* TABELLA E GRAFICI */}
            {loading ? (
              <div
                style={{
                  color: "white",
                  textAlign: "center",
                  padding: "50px",
                  fontSize: "1.5rem",
                }}
              >
                Loading forecast data...
              </div>
            ) : forecastMode === "demand" ? (
              <>
                {sortedForecastData.length === 0 ? (
                  <div className="no-data-message" style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                    No forecast data available
                  </div>
                ) : (
                <>
                <div className="main-table-container forecast-table-container">
                  <table className="main-table">
                    <thead>
                      <tr>
                        {forecastColumns.map(
                          (colName) =>
                            columnVisibility[colName] && (
                              <th
                                key={colName}
                                onClick={() => requestSort(colName)}
                                style={{
                                  cursor: "pointer",
                                  userSelect: "none",
                                }}
                              >
                                {colName}
                                {getSortIndicator(colName)}
                              </th>
                            )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedForecastData.map((row, i) => (
                        <tr key={i}>
                          {columnVisibility["MONTH"] && <td>{formatPeriod(row.year_week)}</td>}
                          {columnVisibility["ACT QTY"] && (
                            <td>{formatNumber(row.actual_qty)}</td>
                          )}
                          {columnVisibility["ACT QTY Y-1"] && (
                            <td className="fcst-Y">
                              {formatNumber(row.actual_qty_last_year)}
                            </td>
                          )}
                          {columnVisibility["FCST QTY - σ"] && (
                            <td className="fcst-minus">
                              {formatNumber(row.fcst_qty_lowerbound)}
                            </td>
                          )}
                          {columnVisibility["FCST QTY (avg)"] && (
                            <td className="fcst-avg">
                              {formatNumber(row.fcst_qty_avg)}
                            </td>
                          )}
                          {columnVisibility["FCST QTY + σ"] && (
                            <td className="fcst-plus">
                              {formatNumber(row.fcst_qty_upperbound)}
                            </td>
                          )}
                          {columnVisibility["ACT VAL Y-1"] && (
                            <td className="fcst-Y">
                              {formatCurrency(row.actual_val_last_year)}
                            </td>
                          )}
                          {columnVisibility["FCST VALUE - σ"] && (
                            <td className="fcst-minus">
                              {formatCurrency(row.fcst_val_lowerbound)}
                            </td>
                          )}
                          {columnVisibility["FCST VALUE (avg)"] && (
                            <td className="fcst-avg">
                              {formatCurrency(row.fcst_val_avg)}
                            </td>
                          )}
                          {columnVisibility["FCST VALUE + σ"] && (
                            <td className="fcst-plus">
                              {formatCurrency(row.fcst_val_upperbound)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="forecast-graphs" key="demand-graphs">
                  <div
                    className="plotly-chart"
                    onClick={() =>
                      handleChartClick("forecast", "qty", "Forecast QTY")
                    }
                  >
                    <Plot
                      data={
                        getForecastChartConfig(
                          "qty",
                          filteredForecastData,
                          false,
                          comparisonData
                        ).traces
                      }
                      layout={
                        getForecastChartConfig(
                          "qty",
                          filteredForecastData,
                          false,
                          comparisonData
                        ).layout
                      }
                      config={
                        getForecastChartConfig(
                          "qty",
                          filteredForecastData,
                          false,
                          comparisonData
                        ).config
                      }
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                  <div
                    className="plotly-chart"
                    onClick={() =>
                      handleChartClick(
                        "forecast",
                        "value",
                        "Forecast VALUE (at AVG COST)"
                      )
                    }
                  >
                    <Plot
                      data={
                        getForecastChartConfig(
                          "value",
                          filteredForecastData,
                          false,
                          comparisonData
                        ).traces
                      }
                      layout={
                        getForecastChartConfig(
                          "value",
                          filteredForecastData,
                          false,
                          comparisonData
                        ).layout
                      }
                      config={
                        getForecastChartConfig(
                          "value",
                          filteredForecastData,
                          false,
                          comparisonData
                        ).config
                      }
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
                </>
                )}
              </>
            ) : (
              <>
                {sortedWinrateData.length === 0 ? (
                  <div className="no-data-message" style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                    No win rate data available
                  </div>
                ) : (
                <>
                <div className="main-table-container forecast-table-container">
                  <table className="main-table">
                    <thead>
                      <tr>
                        {winrateColumns.map(
                          (colName) =>
                            showWrCol(colName) && (
                              <th
                                key={colName}
                                onClick={() => requestSort(colName)}
                                style={{
                                  cursor: "pointer",
                                  userSelect: "none",
                                }}
                              >
                                {colName}
                                {getSortIndicator(colName)}
                              </th>
                            )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWinrateData.map((row, i) => (
                        <tr key={i}>
                          {showWrCol("MONTH") && <td>{formatPeriod(row.year_week)}</td>}
                          {showWrCol("ACT WR Y-1 RAW") && (
                            <td className={`fcst-Y${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>
                              {renderWinrateCell(row, "actual_wr_last_year")}
                            </td>
                          )}
                          {showWrCol("FCST WR (avg) RAW") && (
                            <td className={`fcst-avg${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>
                              {renderWinrateCell(row, "fcst_qty_avg")}
                            </td>
                          )}
                          {showWrCol("ACT WR Y-1 SHRINKED") && (
                            <td className={`fcst-Y${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>
                              {renderWinrateCell(row, "actual_wr_last_year_shrinked")}
                            </td>
                          )}
                          {showWrCol("FCST WR (avg) SHRINKED") && (
                            <td className={`fcst-avg${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>
                              {renderWinrateCell(row, "fcst_qty_avg_shrinked")}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="forecast-graphs forecast-graphs--single" key="winrate-graphs">
                  <div
                    className="plotly-chart"
                    onClick={() =>
                      handleChartClick("winrate", "winrate", "Win Rate %")
                    }
                  >
                    <Plot
                      data={
                        getWinrateChartConfig(
                          filteredWinrateData,
                          false,
                          comparisonData
                        ).traces
                      }
                      layout={
                        getWinrateChartConfig(
                          filteredWinrateData,
                          false,
                          comparisonData
                        ).layout
                      }
                      config={
                        getWinrateChartConfig(
                          filteredWinrateData,
                          false,
                          comparisonData
                        ).config
                      }
                      useResizeHandler={true}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
                </>
                )}
              </>
            )}
          </section>
        </main>
      </div>
      {popupChart && (
        <div
          id="image-popup"
          className="image-popup-overlay"
          style={{ display: "flex" }}
          onClick={closePopup}
        >
          <span id="popup-close" className="popup-close" onClick={closePopup}>
            &times;
          </span>
          <div
            id="popup-chart-container"
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const { type, name, title } = popupChart;

              let chartConfig;
              if (type === "winrate") {
                chartConfig = getWinrateChartConfig(
                  filteredWinrateData,
                  true,
                  comparisonData
                );
              } else {
                chartConfig = getForecastChartConfig(
                  name,
                  filteredForecastData,
                  true,
                  comparisonData
                );
              }

              return (
                <Plot
                  data={chartConfig.traces}
                  layout={{
                    ...chartConfig.layout,
                    title: {
                      text: title,
                      font: { size: 20, color: "#333" },
                      x: 0.5,
                    },
                  }}
                  config={chartConfig.config}
                  style={{ width: "100%", height: "100%" }}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}

export default Forecast;
